/**
 * @license
 * Copyright UX Stack. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at LICENSE.md
 */

import * as uxstackpb from '@uxstack/sdk-proto-js/uxstack/sdk/v1/sdk_pb';
import {
  EventContextAgent,
  EventContextApp,
  EventContextLibrary,
  EventContextLocation,
  EventContextSession,
  EventContextUser,
  EventMessage,
} from '../../event/proto';
import { DOM } from '../../dom';
import { Logger } from '../../logger';
import { BeaconRequest } from '../../request/beacon';
import { FetchRequest } from '../../request/fetch';
import { XHRRequest } from '../../request/xhr';
import { UXStackInterface, UXStackOptions } from '../../sdk';
import { Sender } from '../../sender';
import { Url } from '../../url';
import { UUID } from '../../uuid';
import { Module } from '../module';
import { PageClickPayload } from '../../event/proto/page-click-payload';
import { TrackPayload } from '../../event/proto/track-payload';
import { PagePerformancePayload } from '../../event/proto/page-performance-payload';
import { PageDurationPayload } from '../../event/proto/page-duration-payload';
import { EventPayload } from '../../event/proto/payload';
import { EventBatch } from '../../event/proto/event-batch';
import { PageViewPayload } from '../../event/proto/page-view-payload';
import { VitalsPayload } from '../../event/proto/vitals-payload';
import { Request } from '../../request';

export interface TrackingInterface {
  identify(id: string, properties?: { [key: string]: string }): void;
  vitals(vitals: { [key: string]: string }[]): void;
  page(properties?: { [key: string]: string }): void;
  duration(): void;
  performance(): void;
  track(type: string, action: string, properties?: { [key: string]: string }): void;
  click(event: MouseEvent): void;
  send(event: uxstackpb.EventMessage): void;
  pageChanged(): void;
}

export interface TrackingOptions {
  url?: string;
  senders?: string[];
  adblock: boolean;
}

export class TrackingModule implements Module, TrackingInterface {
  public name: string;

  private correlationId: string;
  private options?: TrackingOptions;
  private agent: EventContextAgent;
  private app: EventContextApp;
  private user: EventContextUser;
  private library: EventContextLibrary;
  private location: EventContextLocation;
  private session: EventContextSession;
  private sdk: UXStackInterface;
  private sender: Sender;
  private host: string;
  private schema: string;
  private senders: { [key: string]: Request };

  constructor() {
    this.name = 'track';
  }

  public load(sdk: UXStackInterface, options: UXStackOptions): void {
    Logger.info('load tracking module');

    this.sdk = sdk;

    this.app = new EventContextApp(sdk.appId, options.version);
    this.agent = new EventContextAgent();
    this.library = new EventContextLibrary('uxstack-sdk.js', VERSION);
    this.session = new EventContextSession(sdk.sessionId);
    this.user = new EventContextUser();

    this.pageChanged();

    if (options.modules !== undefined && (options.modules as object).hasOwnProperty(this.name)) {
      this.options = options.modules[this.name] as TrackingOptions;
    }

    if (this.options.url) {
      const url = Url.parse(this.options.url);
      if (url) {
        this.schema = url.protocol;
        this.host = url.host;
      }
    }

    if (typeof this.options.senders === 'undefined') {
      if (this.options.adblock) {
        this.options.senders = ['fetch', 'xhr'];
      } else {
        this.options.senders = ['beacon', 'fetch', 'xhr'];
      }
    }

    if (typeof this.sender === 'undefined' || this.sender === null) {
      const endpoint = `${this.getUrl(false)}/v1/synapse`;

      this.senders = {
        beacon: new BeaconRequest(endpoint),
        fetch: new FetchRequest(endpoint),
        xhr: new XHRRequest(endpoint),
      };

      this.sender = new Sender(this.getSenders());
    }
  }

  private getSenders(): Request[] {
    return this.options.senders
      .filter((sender) => this.senders.hasOwnProperty(sender))
      .map((sender) => this.senders[sender]);
  }

  /**
   * Identify user
   */
  public identify(id: string, properties?: { [key: string]: string }): void {
    if (!id) {
      throw new Error('User id is required.');
    }

    this.user.id = id;

    this._track(
      'user',
      'identify',
      new TrackPayload({
        properties: properties,
      }),
    );
  }

  /**
   * Track page view
   *
   * @param properties {Object}
   */
  public page(properties?: { [key: string]: string }): void {
    this._track('page', 'view', new PageViewPayload(this.sdk.timestamp));
  }

  public vitals(vitals: { [key: string]: string }[]): void {
    this._track('vitals', 'report', new VitalsPayload(vitals));
  }

  /**
   * Track page duration
   */
  public duration(): void {
    DOM.addEventListener(window, 'unload', () => {
      this._track('page', 'duration', new PageDurationPayload(this.sdk.timestamp));
    });
  }

  /**
   * Track page performance
   */
  public performance(): void {
    if (typeof performance === 'undefined') {
      return;
    }

    this._track('page', 'performance', new PagePerformancePayload());
  }

  public click(event: MouseEvent): void {
    this._track('page', 'click', new PageClickPayload(event));
  }

  /**
   * Track action
   */
  public track(type: string, action: string, properties?: { [key: string]: string }): void {
    this._track(type, action, new TrackPayload({ properties }));
  }

  public send(event: EventMessage): void {
    event.sentAt = BigInt(Date.now());

    const batch = new EventBatch([event]);

    const payload = batch.encode();

    // const payload = EventMessage.encode(event).finish();

    Logger.debug('Event:', event);
    Logger.debug('Event binary:', payload);
    Logger.debug('Event size:', payload.length);

    if (!this.sender.send(payload)) {
      Logger.error('Cannot send event');
    }
  }

  public pageChanged(): void {
    this.location = new EventContextLocation();

    this.correlationId = UUID.v4();
  }

  private _track(type: string, action: string, payload?: EventPayload): void {
    if (this.sdk.isReady === false) {
      Logger.error('Please config Cognitive SDK');

      return;
    }

    if (!type) {
      throw new Error('Type of event is required.');
    }

    if (!action) {
      throw new Error('action of event is required.');
    }

    const event = this.createEvent(type, action);

    Logger.debug('Payload:', payload);

    event.setPayload(payload);

    this.send(event);
  }

  /*
    public addUserProperties(property: string, value: any): void {
        //@TODO: implements this
        return;
    }

    public addEventProperties(property: string, value: any): void {
        //@TODO: implements this
        return;
    }

    public removeEventProperty(property: string): void {
        //@TODO: implements this
        return;
    }

    public clearEventProperties(): void {
        //@TODO: implements this
        return;
    }

    public resetIdentity(): void {
        //@TODO: implements this
        return;
    }

    public setEventProperties(properties: { [key: string]: any }): void {
        //@TODO: implements this
        return;
    }

    public unsetEventProperty(property: string): void {
        //@TODO: implements this
        return;
    }
    */
  private createEvent(type: string, action: string): EventMessage {
    const event = new EventMessage();

    event.correlationId = this.correlationId;
    event.type = type;
    event.action = action;

    event.context.user = this.user;
    event.context.app = this.app;
    event.context.agent = this.agent;
    event.context.session = this.session;
    event.context.library = this.library;
    event.context.location = this.location;

    return event;
  }

  private getUrl(ws: boolean): string {
    let schema = this.schema;

    if (ws && this.schema === 'https') {
      schema = 'wss';
    } else if (ws && this.schema === 'http') {
      schema = 'ws';
    }

    return `${schema}://${this.host}`;
  }
}
